<script setup lang="ts">
import {
  AlertCircle,
  FunnelX,
  Plus,
  RefreshCw,
  Search,
  Trash2,
} from 'lucide-vue-next';
import { computed, onMounted, ref } from 'vue';

// eslint-disable-next-line import/order
import Pagination from '~/components/Pagination';
import ConfirmModal from '~/components/shared/ConfirmModal.vue';
import Spinner from '~/components/Spinner/Spinner.vue';
import DashboardLayout from '~/layouts/DashboardLayout.vue';
import getErrorMessage from '~/utils/getErrorMessage';

import type { AddToBlocklistForm } from '../schemas/blocklistSchema';
import type { BlocklistItem, SortDirection } from '../services/capitalMarketsService';

import AddToBlocklistModal from '../components/AddToBlocklistModal.vue';
import useBlocklistFilters from '../hooks/useBlocklistFilters';
import useAddToBlocklist from '../queries/useAddToBlocklist';
import useBlocklist from '../queries/useBlocklist';
import useRemoveFromBlocklist from '../queries/useRemoveFromBlocklist';

const showAddModal = ref(false);
const showRemoveModal = ref(false);
const selectedCcbToRemove = ref<string>('');

const {
  apiParams,
  handleReset,
  handleSearch,
  initializeForm,
  localCcbSearch,
  localSortDirection,
  setPage,
  setPageSize,
  setSortDirection,
} = useBlocklistFilters();

const {
  data: blocklistData,
  error: blocklistError,
  isError: isBlocklistError,
  isLoading: isLoadingBlocklist,
  refetch: refetchBlocklist,
} = useBlocklist(apiParams);

const addToBlocklistMutation = useAddToBlocklist();
const removeFromBlocklistMutation = useRemoveFromBlocklist();

const blocklistItems = computed<BlocklistItem[]>(() => {
  if (blocklistData.value?.items && blocklistData.value.items.length > 0) {
    return blocklistData.value.items;
  }
  return [];
});

const hasNext = computed(() => !!blocklistData.value?.has_next);
const pageSize = computed(() => Number(apiParams.value.page_size) || 20);
const currentPage = computed(() => Number(apiParams.value.page) || 1);
const hasItems = computed(() => blocklistItems.value.length > 0);
const errorMessage = computed(() => getErrorMessage(blocklistError.value));

function formatDateTime(dateString: string): string {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return dateString;
  }
}

async function handleAddToBlocklist(data: AddToBlocklistForm): Promise<void> {
  await addToBlocklistMutation.mutateAsync(data);
  showAddModal.value = false;
}

async function handleConfirmRemove(): Promise<void> {
  if (selectedCcbToRemove.value) {
    await removeFromBlocklistMutation.mutateAsync(selectedCcbToRemove.value);
    showRemoveModal.value = false;
    selectedCcbToRemove.value = '';
  }
}

function handleOpenAddModal(): void {
  showAddModal.value = true;
}

function handleOpenRemoveModal(ccb: string): void {
  selectedCcbToRemove.value = ccb;
  showRemoveModal.value = true;
}

function handlePageSizeChange(newPageSize: number): void {
  setPageSize(newPageSize);
}

function handleRetry(): void {
  refetchBlocklist();
}

function handleSetPage(value: number): void {
  setPage(value);
}

function handleSortDirectionChange(event: Event): void {
  const target = event.target as HTMLSelectElement;
  setSortDirection(target.value as SortDirection);
}

onMounted(() => {
  document.title = 'Lista de Bloqueios de Financiamentos';
  initializeForm();
});
</script>

<template>
  <DashboardLayout
    title="Lista de Bloqueios"
    description="Gestão de financiamentos bloqueados para cessões"
  >
    <ol class="breadcrumb breadcrumb-arrows mb-2" aria-label="breadcrumbs">
      <li class="breadcrumb-item">
        <router-link to="/cessions">Mercado de Capitais</router-link>
      </li>
      <li class="breadcrumb-item active" aria-current="page">
        <span>Lista de Bloqueios</span>
      </li>
    </ol>

    <template #actions>
      <button
        type="button"
        class="btn btn-primary"
        @click="handleOpenAddModal"
      >
        <Plus class="icon icon-sm me-1" />
        Adicionar à Lista de Bloqueios
      </button>
    </template>

    <div class="row row-cards">
      <div class="col-12">
        <div class="card position-relative">
          <div class="card-body border-bottom py-3">
            <div class="d-flex gap-3 flex-wrap align-items-end">
              <div class="mb-3 flex-grow-1" style="min-width: 200px;">
                <label class="form-label" for="ccb_search">Buscar por CCB</label>
                <input
                  id="ccb_search"
                  v-model="localCcbSearch"
                  type="text"
                  class="form-control"
                  placeholder="Digite o número da CCB"
                  @keydown.enter="handleSearch"
                />
              </div>

              <div class="mb-3 flex-grow-1" style="min-width: 150px;">
                <label class="form-label" for="sort_direction">Ordenar por Data</label>
                <select
                  id="sort_direction"
                  class="form-select"
                  :value="localSortDirection"
                  @change="handleSortDirectionChange"
                >
                  <option value="desc">Mais recentes</option>
                  <option value="asc">Mais antigas</option>
                </select>
              </div>

              <div class="mb-3 d-flex gap-2 button-group">
                <button
                  type="button"
                  class="btn btn-primary"
                  @click="handleSearch"
                >
                  <Search class="icon icon-sm me-1" />
                  Buscar
                </button>
                <button
                  type="button"
                  class="btn btn-secondary"
                  @click="handleReset"
                >
                  <FunnelX class="icon icon-sm me-1" />
                  Limpar
                </button>
              </div>
            </div>
          </div>

          <div v-if="isBlocklistError && !isLoadingBlocklist" class="card-body error-state">
            <div class="alert alert-danger" role="alert">
              <div class="alert-content">
                <div class="d-flex align-items-center gap-2 mb-3">
                  <AlertCircle class="icon icon-sm" />
                  <strong>Erro ao carregar lista de bloqueios</strong>
                </div>
                <p class="mb-3">
                  Ocorreu um erro ao carregar a lista de financiamentos bloqueados. Por favor, tente novamente.
                </p>
                <div v-if="errorMessage && errorMessage !== 'UNKNOWN'" class="mb-3">
                  <small class="text-muted">Detalhes: {{ errorMessage }}</small>
                </div>
                <button
                  type="button"
                  class="btn btn-primary"
                  @click="handleRetry"
                >
                  <RefreshCw class="icon icon-sm me-1 text-white" />
                  Tentar novamente
                </button>
              </div>
            </div>
          </div>

          <div v-else-if="!isLoadingBlocklist && !hasItems" class="empty-state">
            <div class="empty-state-content">
              <AlertCircle class="icon icon-lg text-muted" />
              <p class="text-secondary mb-0">
                <strong>Nenhum financiamento bloqueado encontrado</strong>
              </p>
              <p class="text-muted small mb-0">
                Não há financiamentos na lista de bloqueios no momento.
              </p>
            </div>
          </div>

          <div v-else-if="!isLoadingBlocklist && hasItems" class="table-responsive">
            <table class="table table-vcenter">
              <thead>
                <tr>
                  <th scope="col">Financiamento ID</th>
                  <th scope="col">CCB</th>
                  <th scope="col">Motivo do Bloqueio</th>
                  <th scope="col">Criado por</th>
                  <th scope="col">Data de Criação</th>
                  <th scope="col" class="text-end">Ações</th>
                </tr>
              </thead>

              <tbody>
                <tr v-for="item in blocklistItems" :key="item.id">
                  <td>
                    <strong>{{ item.solfacil_financing_id }}</strong>
                  </td>
                  <td>
                    <strong>{{ item.ccb }}</strong>
                  </td>
                  <td>
                    <span class="text-secondary">{{ item.reason }}</span>
                  </td>
                  <td>
                    <span class="text-secondary">{{ item.created_by || '-' }}</span>
                  </td>
                  <td>
                    <span class="text-secondary">{{ formatDateTime(item.created_at) }}</span>
                  </td>
                  <td class="text-end">
                    <button
                      type="button"
                      class="btn btn-sm btn-danger"
                      @click="handleOpenRemoveModal(item.ccb)"
                      :disabled="removeFromBlocklistMutation.isPending.value"
                    >
                      <Trash2 class="icon icon-sm" />
                      Remover
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-else-if="isLoadingBlocklist" class="loading-state">
            <Spinner />
            <p class="text-muted mt-2 mb-0">Carregando lista de bloqueios...</p>
          </div>

          <div class="pagination-wrapper">
            <Pagination
              v-if="!isBlocklistError && hasItems"
              :current-page="currentPage"
              :has-next="hasNext"
              :page-size="pageSize"
              @page-change="handleSetPage"
              @page-size-change="handlePageSizeChange"
            />
          </div>
        </div>
      </div>
    </div>

    <AddToBlocklistModal
      v-model="showAddModal"
      :is-loading="addToBlocklistMutation.isPending.value"
      @submit="handleAddToBlocklist"
    />

    <ConfirmModal
      v-model="showRemoveModal"
      title="Remover da Lista de Bloqueios"
      message="Tem certeza que deseja remover este financiamento? Esta ação não pode ser desfeita."
      type="danger"
      confirm-text="Remover"
      :loading="removeFromBlocklistMutation.isPending.value"
      @confirm="handleConfirmRemove"
    />
  </DashboardLayout>
</template>

<style scoped lang="scss">
$mobile: 576px;
$tablet: 768px;

.btn-primary {
  @media (max-width: $mobile) {
    max-width: 165px;
    white-space: normal;
    word-wrap: break-word;
    line-height: 1.2;
    padding: 0.5rem;
  }
}

.table-responsive {
  min-height: 50vh;
  overflow-x: auto;
  overflow-y: auto;
}

.card-body {
  .button-group {
    flex-shrink: 0;
    white-space: nowrap;

    @media (max-width: $mobile) {
      width: 100%;
      justify-content: stretch;

      button {
        flex: 1;
      }
    }
  }

  &.error-state {
    padding: 2rem;
    min-height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;

    .alert {
      width: 100%;
      max-width: 600px;
      margin: 0 auto;
      border: 1px solid #f5c6cb;
      border-radius: 0.5rem;
      padding: 1.5rem;

      .alert-content {
        .icon {
          flex-shrink: 0;
          color: #dc3545;
        }

        strong {
          color: #721c24;
        }

        p {
          color: #721c24;
          margin-bottom: 1rem;
        }

        small {
          display: block;
          color: #6c757d;
          font-size: 0.875rem;
        }

        .btn {
          margin-top: 0.5rem;

          .text-white {
            color: white !important;
            stroke: white;
          }
        }
      }
    }
  }
}

.icon-lg {
  width: 3rem;
  height: 3rem;
}

.empty-state {
  min-height: 50vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;

  .empty-state-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    text-align: center;
  }
}

.loading-state {
  min-height: 50vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  text-align: center;
}

.table {
  tbody {
    tr {
      background-color: white !important;
      transition: background-color 0.2s ease;

      &:hover {
        background-color: #f8f9fa !important;
      }
    }
  }

  th,
  td {
    white-space: nowrap;

    @media (max-width: $tablet) {
      white-space: normal;
      font-size: 0.875rem;
    }
  }
}

@media (max-width: 768px) {
  .table-responsive {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .table {
    min-width: 800px;
  }

  .pagination-wrapper {
    display: flex;
    justify-content: center;
    width: 100%;
    overflow-x: auto;
    padding: 0 1rem;
  }

  .pagination-wrapper :deep(.d-flex) {
    flex-wrap: wrap;
    justify-content: center !important;
    padding-right: 0 !important;
    width: 100%;
  }
}

@media (min-width: 769px) {
  .pagination-wrapper {
    display: flex;
    justify-content: flex-end;
    width: 100%;
  }
}
</style>
